import { AxiosResponse } from 'axios'
import apiClient from 'api/common/common'
import { Status } from 'api/common/statuses'

// ============================================
// 型定義
// ============================================

/** タイトル条件 - 育成条件の要素 */
export interface TrainingCondition {
  titleId: number
  titleName: string
  abbreviation: string
  count: number
}

/** タイトル条件 - バイナリー単月条件 */
export interface BinarySingleCondition {
  amountTotal: number | null
  amount: number | null
  countTotal: number | null
  count: number | null
}

/** タイトル条件 - バイナリー連月条件 */
export interface BinaryConsecutiveCondition {
  amountTotal: number | null
  amount: number | null
  countTotal: number | null
  count: number | null
}

/** タイトル条件 - バイナリー条件 */
export interface BinaryCondition {
  single: BinarySingleCondition
  consecutive: BinaryConsecutiveCondition
  training: TrainingCondition[]
}

/** タイトル条件 - ユニレベル条件 */
export interface UnilevelCondition {
  amountTotal: number | null
  training: TrainingCondition[]
  trainingAmount: number | null
}

/** タイトル条件 - 維持条件 */
export interface MaintenanceCondition {
  amountTotal: number | null
  amount: number | null
}

/** タイトル条件（1タイトル分） */
export interface TitleCondition {
  titleId: number
  titleName: string
  abbreviation: string
  binary: BinaryCondition | null
  unilevel: UnilevelCondition | null
  maintenance: MaintenanceCondition | null
}

/** タイトル条件取得APIレスポンス */
export interface TitleConditionsResponse {
  status: string
  errors: string[]
  response: TitleCondition[]
}

// --------------------------------------------
// バイナリー昇格条件
// --------------------------------------------

/** 左右の値 */
export interface LeftRightValue {
  left: number
  right: number
}

/** バイナリー昇格条件 - 育成条件の実績 */
export interface BinaryTrainingActual {
  titleId: number
  titleName: string
  leftCount: number
  rightCount: number
}

/** バイナリー昇格条件 - 条件実績 */
export interface BinaryPromotionConditions {
  singleMonth: {
    referralCount: LeftRightValue
    totalAmount: LeftRightValue
  }
  consecutiveMonth: {
    referralCount: LeftRightValue
    totalAmount: LeftRightValue
  }
  training: {
    titles: BinaryTrainingActual[]
  }
}

/** バイナリー昇格条件取得APIレスポンス */
export interface BinaryPromotionResponse {
  status: string
  errors: string[]
  response: {
    positionId: number
    positionTitleId: number
    conditions: BinaryPromotionConditions
  } | null
}

// --------------------------------------------
// ユニレベル昇格条件
// --------------------------------------------

/** ユニレベル昇格条件 - 育成条件の実績 */
export interface UnilevelTrainingActual {
  titleId: number
  titleName: string
  count: number
  amount: number
  seriesAmount: number
}

/** ユニレベル昇格条件 - 条件実績 */
export interface UnilevelPromotionConditions {
  amount: number
  training: UnilevelTrainingActual[]
}

/** ユニレベル昇格条件取得APIレスポンス */
export interface UnilevelPromotionResponse {
  status: string
  errors: string[]
  response: {
    positionId: number
    positionTitleId: number
    conditions: UnilevelPromotionConditions
  } | null
}

// --------------------------------------------
// 維持条件
// --------------------------------------------

/** 維持条件取得APIレスポンス */
export interface MaintenanceConditionResponse {
  status: string
  errors: string[]
  response: {
    positionId: number
    positionTitleId: number
    amounts: LeftRightValue
  } | null
}

// ============================================
// API関数
// ============================================

/**
 * タイトル条件取得API
 * 全タイトルの昇格条件・維持条件の目標値（閾値）を取得
 */
export const getTitleConditions = async (): Promise<TitleCondition[] | null> => {
  try {
    const response: AxiosResponse<TitleCondition[]> = await apiClient.get('/titles/conditions')
    const status = response?.headers['x-aqc-status'] ?? 'failed'
    if (status === Status.Success && response.data) {
      return response.data
    }
    return null
  } catch (error) {
    console.error('Failed to fetch title conditions:', error)
    return null
  }
}

/**
 * バイナリー昇格条件取得API
 * 指定ポジションのバイナリー昇格条件の達成状況（実績値）を取得
 */
export const getBinaryPromotionConditions = async (
  positionId: number
): Promise<BinaryPromotionResponse['response'] | null> => {
  try {
    const response: AxiosResponse<BinaryPromotionResponse['response']> = await apiClient.get(
      `/promotionConditions/binary/${positionId}`
    )
    const status = response?.headers['x-aqc-status'] ?? 'failed'
    if (status === Status.Success && response.data) {
      return response.data
    }
    return null
  } catch (error) {
    console.error('Failed to fetch binary promotion conditions:', error)
    return null
  }
}

/**
 * ユニレベル昇格条件取得API
 * 指定ポジションのユニレベル昇格条件の達成状況（実績値）を取得
 */
export const getUnilevelPromotionConditions = async (
  positionId: number
): Promise<UnilevelPromotionResponse['response'] | null> => {
  try {
    const response: AxiosResponse<UnilevelPromotionResponse['response']> = await apiClient.get(
      `/promotionConditions/unilevel/${positionId}`
    )
    const status = response?.headers['x-aqc-status'] ?? 'failed'
    if (status === Status.Success && response.data) {
      return response.data
    }
    return null
  } catch (error) {
    console.error('Failed to fetch unilevel promotion conditions:', error)
    return null
  }
}

/**
 * 維持条件取得API
 * 指定ポジションの維持条件の達成状況（実績値）を取得
 */
export const getMaintenanceConditions = async (
  positionId: number
): Promise<MaintenanceConditionResponse['response'] | null> => {
  try {
    const response: AxiosResponse<MaintenanceConditionResponse['response']> = await apiClient.get(
      `/maintenanceConditions/${positionId}`
    )
    const status = response?.headers['x-aqc-status'] ?? 'failed'
    if (status === Status.Success && response.data) {
      return response.data
    }
    return null
  } catch (error) {
    console.error('Failed to fetch maintenance conditions:', error)
    return null
  }
}

/**
 * 条件確認画面で必要な全データを一括取得
 */
export const getConditionCheckData = async (positionId: number) => {
  const [titleConditions, binaryConditions, unilevelConditions, maintenanceConditions] = await Promise.all([
    getTitleConditions(),
    getBinaryPromotionConditions(positionId),
    getUnilevelPromotionConditions(positionId),
    getMaintenanceConditions(positionId),
  ])

  return {
    titleConditions,
    binaryConditions,
    unilevelConditions,
    maintenanceConditions,
  }
}
